import { resolvePortfolioAppHost } from "./portfolio-app-host";
import { resolveMarketRouteSurface } from "./market-route-boundary";

export { resolvePortfolioAppHost } from "./portfolio-app-host";

/**
 * Set VITE_PORTFOLIO_APP_HOST on a separate staging build to give Portfolio
 * its own staging origin. Leave it unset on production builds.
 */
export const PORTFOLIO_APP_HOST = resolvePortfolioAppHost(
  import.meta.env.VITE_PORTFOLIO_APP_HOST,
);
export const EVERYDAY_BULLION_TENANT_SLUG = "everyday-bullion";

export type AppSurface = "operations" | "portfolio";

function isPathOrDescendant(pathname: string, root: string): boolean {
  return pathname === root || pathname.startsWith(`${root}/`);
}

/**
 * Customer shop and account pages use their own public/customer session model.
 * They must not initialize the operator Clerk session or operator data
 * providers, even when served from the same origin as the staff workspace.
 */
export function isCustomerShopOrAccountPath(pathname: string): boolean {
  return (
    isPathOrDescendant(pathname, "/shop") ||
    isPathOrDescendant(pathname, "/portal") ||
    isPathOrDescendant(pathname, "/portfolio") ||
    pathname === "/wholesale/shop" ||
    pathname === "/account/wholesale"
  );
}

/**
 * Public marketing pages, published market pages, and market embeds do not
 * need the operator authentication or data-provider tree.
 */
export function isPublicMarketingOrMarketPath(pathname: string): boolean {
  return (
    isPathOrDescendant(pathname, "/site") ||
    resolveMarketRouteSurface(pathname) === "public" ||
    /^\/widgets\/market\/[^/]+\/?$/.test(pathname)
  );
}

function hostnameOnly(hostname: string): string {
  return hostname.toLowerCase().replace(/:\d+$/, "").replace(/\.$/, "");
}

export function isPortfolioAppHost(
  hostname: string,
  portfolioAppHost = PORTFOLIO_APP_HOST,
): boolean {
  return hostnameOnly(hostname) === hostnameOnly(portfolioAppHost);
}

/**
 * The portfolio app owns a separate origin in production. Replit previews and
 * local development remain same-origin so the customer surface can still be
 * exercised without DNS or certificate setup.
 */
export function isProductionMetalUiHost(hostname: string): boolean {
  const host = hostnameOnly(hostname);
  return host === "metalui.co.uk" || host === "www.metalui.co.uk" || host.endsWith(".metalui.co.uk");
}

export function portfolioAppUrl(
  location: Pick<Location, "hostname" | "protocol" | "pathname" | "search" | "hash">,
  portfolioAppHost = PORTFOLIO_APP_HOST,
): string {
  const targetHost = hostnameOnly(portfolioAppHost);
  const target = new URL(`${location.protocol}//${targetHost}`);
  target.pathname = canonicalPortfolioPath(location.pathname || "/portfolio");
  target.search = location.search;
  target.hash = location.hash;
  const sourceHost = hostnameOnly(location.hostname);
  const suffix = ".metalui.co.uk";
  if (target.searchParams.has("tenant")) return target.toString();

  if (sourceHost === "metalui.co.uk" || sourceHost === "www.metalui.co.uk") {
    target.searchParams.set("tenant", EVERYDAY_BULLION_TENANT_SLUG);
  } else if (sourceHost.endsWith(suffix) && sourceHost !== targetHost) {
    const slug = sourceHost.slice(0, -suffix.length);
    if (slug && slug !== "www" && /^[a-z0-9-]+$/.test(slug)) {
      target.searchParams.set("tenant", slug);
    }
  }
  return target.toString();
}

export function isPortfolioPath(pathname: string): boolean {
  return pathname === "/portfolio" || pathname.startsWith("/portfolio/");
}

export function isLegacyPortalPortfolioPath(pathname: string): boolean {
  return pathname === "/portal/portfolio" || pathname.startsWith("/portal/portfolio/");
}

export function canonicalPortfolioPath(pathname: string): string {
  if (!isLegacyPortalPortfolioPath(pathname)) return pathname;
  const tail = pathname.slice("/portal/portfolio".length);
  return tail ? `/portfolio${tail}` : "/portfolio/dashboard";
}

export function portfolioDocumentTitle(name: string): string {
  const trimmed = name.trim();
  return /\bportfolio$/i.test(trimmed) ? trimmed : `${trimmed} Portfolio`;
}

export function shouldRedirectLegacyPortfolio(
  location: Pick<Location, "hostname" | "pathname">,
  portfolioAppHost = PORTFOLIO_APP_HOST,
): boolean {
  return (
    !isPortfolioAppHost(location.hostname, portfolioAppHost) &&
    isProductionMetalUiHost(location.hostname) &&
    (isPortfolioPath(location.pathname) || isLegacyPortalPortfolioPath(location.pathname))
  );
}